import { AccountState } from './_store/account/account.state';
import { ErrorHandlerService } from '@app/logging';
import {
  OrganizationState,
  ActivityState,
  MembersState,
  DialogState,
  ToastState,
  ParticipantState,
  ServerEventsState,
  RegistrationState,
  SupportState,
  AdminState,
} from '@app/_store';
import { NgModule, DEFAULT_CURRENCY_CODE, LOCALE_ID, ErrorHandler } from '@angular/core';
import { IPublicClientApplication, PublicClientApplication, InteractionType, AuthenticationScheme } from '@azure/msal-browser';
import {
  MsalGuard,
  MsalBroadcastService,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MSAL_INSTANCE,
  MsalGuardConfiguration,
  MsalRedirectComponent,
  MsalModule,
  MsalInterceptorConfiguration,
  MsalInterceptor,
} from '@azure/msal-angular';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { JwtInterceptor, PreviousRouteService } from './_helpers';
import { NgxsModule } from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsActionsExecutingModule } from '@ngxs-labs/actions-executing';
import { environment } from '@environments/environment';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';
import { HomeNavigationComponent } from './components/home/home-navigation/home-navigation.component';
import { OrganizationNavigationComponent } from './components/organization/organization-navigation/organization-navigation.component';
import { ActivityNavigationComponent } from './components/organization/activities/activity/activity-navigation/activity-navigation.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { registerLocaleData, DatePipe, CurrencyPipe } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import localeSvExtra from '@angular/common/locales/extra/sv';
import { loginRequest, msalConfig } from './auth-config';
import { CookieService } from 'ngx-cookie-service';

registerLocaleData(localeSv, 'sv', localeSvExtra);

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

export function MSALGuardConfigFactory(cookieService: CookieService): MsalGuardConfiguration {
  const lastProvider = cookieService.get('preferred-idp');
  if (lastProvider) loginRequest['extraQueryParameters'] = { domain_hint: lastProvider };

  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();

  // MSAL 5+ matches strictly: a bare base URL no longer covers its sub-paths, so register both
  environment.authentication.protectedResources.forEach(item => {
    protectedResourceMap.set(item.resource, item.scopes);
    protectedResourceMap.set(`${item.resource}/*`, item.scopes);
  });

  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      authenticationScheme: AuthenticationScheme.BEARER,
    },
    protectedResourceMap,
    strictMatching: true,
  };
}

@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    NgxsModule.forRoot(
      [
        AccountState,
        OrganizationState,
        MembersState,
        ActivityState,
        ParticipantState,
        RegistrationState,
        ToastState,
        DialogState,
        ServerEventsState,
        SupportState,
        AdminState,
      ],
      {
        developmentMode: !environment.production,
      }
    ),
    NgxsRouterPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    NgxsActionsExecutingModule.forRoot(),
    MsalModule,
    // Only what AppComponent's own template uses; every page is loaded through the router (most of them lazily)
    ToastContainerComponent,
    ConfirmDialogComponent,
    HomeNavigationComponent,
    OrganizationNavigationComponent,
    ActivityNavigationComponent,
  ],
  declarations: [AppComponent],
  providers: [
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    CookieService,
    DatePipe,
    CurrencyPipe,
    PreviousRouteService,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    // { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'SEK' },
    { provide: LOCALE_ID, useValue: 'sv' },
    { provide: ErrorHandler, useClass: ErrorHandlerService },
    {
      provide: HTTP_INTERCEPTORS, // Provides as HTTP Interceptor
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [CookieService],
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
